import { runtimeEnv } from '../../runtimeEnv';
import { deleteCookies } from '../consent/cookies';

// GA4 has no official npm package, so we inject gtag.js ourselves. window + the script
// loader are injectable for tests. Gated: initGa4 runs only once this browser has consented,
// whether that was just now or on an earlier visit.
export type Ga4Window = { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void } & Record<string, unknown>;
type LoadScript = (src: string) => void;
type ClearCookies = (names: string[]) => void;

let loaded = false; // gtag.js injected on this page
let started = false; // collecting

export function initGa4(
  measurementId: string = runtimeEnv.ga4MeasurementId,
  win: Ga4Window = globalThis,
  loadScript: LoadScript = defaultLoadScript,
): void {
  if (!measurementId || started) return;
  if (!loaded) {
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`);
    loaded = true;
  }
  const dataLayer = win.dataLayer ?? [];
  win.dataLayer = dataLayer;
  // gtag.js only treats an `arguments` object as a command; a plain array is ignored.
  const gtag: (...args: unknown[]) => void = function () {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };
  win.gtag = gtag;
  win[`ga-disable-${measurementId}`] = false; // undo a prior stop's runtime opt-out
  gtag('js', new Date());
  // Default cookie domain ('auto') puts `_ga` on the registrable domain, so www ↔ hub share
  // one visit with no extra config.
  gtag('config', measurementId);
  started = true;
}

// Fired at signup completion. No-op unless GA4 is consented + started.
export function trackSignUp(win: Ga4Window = globalThis): void {
  if (!started || !win.gtag) return;
  win.gtag('event', 'sign_up');
}

export function stopGa4(
  measurementId: string = runtimeEnv.ga4MeasurementId,
  win: Ga4Window = globalThis,
  clear: ClearCookies = deleteCookies,
): void {
  if (measurementId) win[`ga-disable-${measurementId}`] = true; // GA4's runtime opt-out flag
  started = false;
  clear(['_ga', `_ga_${measurementId.replace(/^G-/, '')}`, '_gid']);
}

function defaultLoadScript(src: string): void {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}
